/**
 * AC-1599 / AC-1594: CloneProjectModal.
 *
 * Renders a modal that lets the user clone an existing project. The modal
 * calls the AC-1597 backend endpoint via `useCloneProject()` and, on success,
 * closes itself and navigates to the newly-created project's page.
 *
 * Behavior contract (locked by
 * `frontend/src/components/projects/__tests__/CloneProjectModal.test.tsx`):
 *
 *   - Name input pre-fills to `"{sourceProject.name} (copy)"`.
 *   - Payload is `{ name }` only — the backend's `ProjectCloneRequest`
 *     schema does not accept any other fields. Tests assert that
 *     `Object.keys(payload)` equals `['name']`.
 *   - On success: call `onClose()` then `navigate('/projects/{new_project_id}')`.
 *   - On 409 (name collision): inline error mentioning "already exists";
 *     do NOT close or navigate.
 *   - On 404 / 500 / other: a generic error appears; do NOT close or navigate.
 *   - Submit disabled when name is empty (after trim).
 *   - Loading spinner / `aria-busy` button while the mutation is in flight.
 *   - Cancel button and Close (X) button call `onClose` without submitting.
 *   - Container has `role="dialog"` and `aria-modal="true"`.
 *   - Close (X) button has `aria-label="Close"`.
 *
 * Mirrors `CopyWorkflowModal.tsx` (AC-1598) for styling and accessibility
 * patterns; differs in that there is no project picker (clone target is
 * always a new project owned by the same user) and the payload carries only
 * `name`.
 */
import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useModalDismiss } from '../../hooks/useModalDismiss';

import { useCloneProject } from '../../hooks/queries/useProjectMutations';

export interface CloneProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceProject: {
    project_id: number;
    name: string;
  };
}

/**
 * Extract a user-friendly error message from an axios-style error object.
 * Falls back to a generic message when the backend's detail is not present.
 */
function describeError(err: unknown): { isCollision: boolean; message: string } {
  const errObj = err as
    | {
        response?: { status?: number; data?: { detail?: string } };
        message?: string;
      }
    | undefined;
  const status = errObj?.response?.status;
  const detail = errObj?.response?.data?.detail;

  if (status === 409) {
    return {
      isCollision: true,
      message: detail || 'A project with that name already exists.',
    };
  }
  return {
    isCollision: false,
    message: detail || 'Failed to clone project. Please try again.',
  };
}

const CloneProjectModal: React.FC<CloneProjectModalProps> = ({
  isOpen,
  onClose,
  sourceProject,
}) => {
  const navigate = useNavigate();
  const cloneMutation = useCloneProject();

  const [name, setName] = useState<string>(`${sourceProject.name} (copy)`);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Defensive reset for parents that keep the modal mounted across open/close
  // cycles. Without this, the previous source-project's pre-fill leaks into
  // the next open. Mirrors the AC-1598 CopyWorkflowModal pattern.
  useEffect(() => {
    if (isOpen) {
      setName(`${sourceProject.name} (copy)`);
      setErrorMessage(null);
    }
  }, [isOpen, sourceProject]);

  const isPending = cloneMutation.isPending;

  // AC-1602: Escape key + backdrop click dismiss, focus restore on close.
  // Hook must be called before the `!isOpen` early return so the rules of
  // hooks are honoured across renders. Hook itself no-ops when isOpen=false.
  const { backdropProps } = useModalDismiss({
    isOpen,
    onClose,
    isBlocked: isPending,
  });

  if (!isOpen) return null;

  const isSubmitDisabled = name.trim() === '' || isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    setErrorMessage(null);
    try {
      const result = await cloneMutation.mutateAsync({
        projectId: sourceProject.project_id,
        payload: {
          name: name.trim(),
        },
      });
      onClose();
      navigate(`/projects/${result.project_id}`);
    } catch (err) {
      const { message } = describeError(err);
      setErrorMessage(message);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0"
        {...backdropProps}
        data-testid="modal-backdrop"
      >
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 dark:bg-black opacity-75"></div>
        </div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white dark:bg-cap-dark rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              aria-label="Close"
              className="bg-white dark:bg-cap-dark rounded-md text-gray-400 hover:text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-warm-white">
                Clone Project
              </h3>
              <div className="mt-4">
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  autoComplete="off"
                >
                  <div>
                    <label
                      htmlFor="clone-project-name"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="clone-project-name"
                      name="clone-project-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  {errorMessage && (
                    <div
                      role="alert"
                      className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300"
                    >
                      {errorMessage}
                    </div>
                  )}

                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={isSubmitDisabled}
                      aria-busy={isPending}
                      className="w-full inline-flex items-center justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isPending && (
                        <Loader2
                          className="w-4 h-4 mr-2 animate-spin"
                          role="status"
                          aria-label="Cloning"
                        />
                      )}
                      {isPending ? 'Cloning...' : 'Clone'}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-cap-dark text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloneProjectModal;
